import React, { createContext, useContext, useState, useEffect } from 'react';
import { Project, Task, ProjectStatus, TaskStatus, TaskPriority, ProjectRole, Permission } from '@/types';

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  tasks: Task[];
  setCurrentProject: (project: Project | null) => void;
  addProject: (project: Omit<Project, 'id' | 'createdAt'>) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  getProjectTasks: (projectId: string) => Task[];
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

// Mock data inicial
const mockProjects: Project[] = [
  {
    id: '1',
    name: 'Website Redesign',
    description: 'Redesign do site corporativo com nova identidade visual',
    startDate: new Date('2024-01-15'),
    endDate: new Date('2024-03-15'),
    estimatedDuration: 60,
    actualDuration: 45,
    status: ProjectStatus.IN_PROGRESS,
    progress: 75,
    members: [
      {
        userId: '1',
        role: ProjectRole.OWNER,
        permissions: [Permission.CREATE_TASKS, Permission.EDIT_TASKS, Permission.DELETE_TASKS, Permission.ASSIGN_TASKS, Permission.VIEW_REPORTS, Permission.MANAGE_MEMBERS],
        joinedAt: new Date('2024-01-15')
      },
      {
        userId: '2',
        role: ProjectRole.EDITOR,
        permissions: [Permission.CREATE_TASKS, Permission.EDIT_TASKS, Permission.ASSIGN_TASKS],
        joinedAt: new Date('2024-01-16')
      }
    ],
    createdBy: '1',
    createdAt: new Date('2024-01-15')
  },
  {
    id: '2',
    name: 'Mobile App Development',
    description: 'Desenvolvimento de aplicativo mobile para iOS e Android',
    startDate: new Date('2024-02-01'),
    endDate: new Date('2024-06-01'),
    estimatedDuration: 120,
    status: ProjectStatus.PLANNING,
    progress: 15,
    members: [
      {
        userId: '2',
        role: ProjectRole.OWNER,
        permissions: [Permission.CREATE_TASKS, Permission.EDIT_TASKS, Permission.DELETE_TASKS, Permission.ASSIGN_TASKS, Permission.VIEW_REPORTS, Permission.MANAGE_MEMBERS],
        joinedAt: new Date('2024-02-01')
      }
    ],
    createdBy: '2',
    createdAt: new Date('2024-02-01')
  }
];

const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Análise de Requisitos',
    description: 'Levantar todos os requisitos do novo design',
    projectId: '1',
    assignedTo: '2',
    createdBy: '1',
    status: TaskStatus.DONE,
    priority: TaskPriority.HIGH,
    startDate: new Date('2024-01-15'),
    endDate: new Date('2024-01-20'),
    estimatedHours: 20,
    actualHours: 18,
    progress: 100,
    dependencies: [],
    tags: ['análise', 'requisitos'],
    comments: [],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: '2',
    title: 'Criação de Wireframes',
    description: 'Desenvolver wireframes para todas as páginas',
    projectId: '1',
    assignedTo: '2',
    createdBy: '1',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    startDate: new Date('2024-01-21'),
    endDate: new Date('2024-02-05'),
    estimatedHours: 30,
    actualHours: 25,
    progress: 80,
    dependencies: ['1'],
    tags: ['design', 'wireframes'],
    comments: [],
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-02-03')
  },
  {
    id: '3',
    title: 'Implementação Frontend',
    description: 'Codificar a interface do usuário',
    projectId: '1',
    assignedTo: '3',
    createdBy: '1',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    startDate: new Date('2024-02-06'),
    endDate: new Date('2024-02-28'),
    estimatedHours: 60,
    progress: 0,
    dependencies: ['2'],
    tags: ['frontend', 'react'],
    comments: [],
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-01-21')
  }
];

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>(mockProjects);
  const [tasks, setTasks] = useState<Task[]>(mockTasks);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  const addProject = (projectData: Omit<Project, 'id' | 'createdAt'>) => {
    const newProject: Project = {
      ...projectData,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setProjects(prev => [...prev, newProject]);
    console.log('Project added:', newProject);
  };

  const updateProject = (id: string, updates: Partial<Project>) => {
    setProjects(prev => 
      prev.map(project => 
        project.id === id ? { ...project, ...updates } : project
      )
    );
    console.log('Project updated:', id, updates);
  };

  const deleteProject = (id: string) => {
    setProjects(prev => prev.filter(project => project.id !== id));
    setTasks(prev => prev.filter(task => task.projectId !== id));
    if (currentProject?.id === id) {
      setCurrentProject(null);
    }
    console.log('Project deleted:', id);
  };

  const addTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTask: Task = {
      ...taskData,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setTasks(prev => [...prev, newTask]);
    console.log('Task added:', newTask);
  };

  const updateTask = (id: string, updates: Partial<Task>) => {
    setTasks(prev => 
      prev.map(task => 
        task.id === id ? { ...task, ...updates, updatedAt: new Date() } : task
      )
    );
    console.log('Task updated:', id, updates);
  };

  const deleteTask = (id: string) => {
    setTasks(prev => prev.filter(task => task.id !== id));
    console.log('Task deleted:', id);
  };

  const getProjectTasks = (projectId: string) => {
    return tasks.filter(task => task.projectId === projectId);
  };

  return (
    <ProjectContext.Provider value={{
      projects,
      currentProject,
      tasks,
      setCurrentProject,
      addProject,
      updateProject,
      deleteProject,
      addTask,
      updateTask,
      deleteTask,
      getProjectTasks
    }}>
      {children}
    </ProjectContext.Provider>
  );
};